import React, { useState, useEffect } from 'react';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useOptionalNavigation } from '../contexts/NavigationContext';
import { useProfile } from '../contexts/ProfileContext'; // 🎯 JAN 23: For user name
import { DataService } from '../services/dataService';
import { ContextualChatButton } from '../components/ContextualChatButton';
import { SimpleWorkoutCard } from '../components/cards/SimpleWorkoutCard';
import { SimpleMobilityCard } from '../components/cards/SimpleMobilityCard';
import { DailyRoutineCard } from '../components/cards/DailyRoutineCard';  // 🔄 JAN 21, 2026
import { RestDayCardWrapper } from '../components/cards/RestDayCardWrapper';
import { storage } from '../services/storage';
import { WorkoutIcon, getWorkoutIconType } from '../utils/workoutIconMapper';
import { toLocalDateStr, CHILD_MOBILITY_RE } from '../utils/sharedFormatters';
import type { Workout, MobilityRoutine, RestDay } from '../types';

interface PlanDetailProps {
  planId?: string; // 🔧 FIX JAN 21: Make optional for URL routing
}

/**
 * Outer shell: resolves the plan id and guards on it. It deliberately owns no
 * state and no effects.
 *
 * 2026-09-17 (hooks debt): the guard used to sit INSIDE the component, above a
 * useState and a useEffect. `planId` can be missing on one render and present
 * on the next (the StrictMode reconciliation pass the guard exists for), and a
 * component that renders N hooks once and N+2 the next time makes React throw
 * "Rendered more hooks than during the previous render". Splitting the guard
 * from the hooks removes the class: every hook in PlanDetailLoaded runs on
 * every render of PlanDetailLoaded.
 */
export const PlanDetail: React.FC<PlanDetailProps> = (props) => {
  // 🔧 STRICTMODE FIX (Jan 21, 2026): Handle React reconciliation passes
  const { planId: propPlanId } = props || {};
  const { id } = useLocalSearchParams<{ id: string }>();
  const planId = propPlanId || id; // 🔧 FIX JAN 21: Support both prop and URL param

  // Skip render if no planId (React reconciliation pass or missing data)
  if (!planId) {
    console.log('⏭️ PlanDetail: Skipping render (no planId)');
    return null;
  }

  return <PlanDetailLoaded planId={planId} />;
};

const PlanDetailLoaded: React.FC<{ planId: string }> = ({ planId }) => {
  // 🔧 FIX JAN 23: Make NavigationContext optional with fallbacks
  // 2026-09-17: was a try/catch around useNavigation() — a hook inside a try
  // block is a Rules-of-Hooks violation. useOptionalNavigation() is the same
  // contract (null when no provider) called unconditionally.
  const navigationContext = useOptionalNavigation();

  const router = useRouter();
  const { profile } = useProfile(); // 🎯 JAN 23: Get user profile for name

  // Smart back navigation based on source
  // 🔧 FIX FEB 4, 2026: Use NavigationContext goBack() ONLY for stateful overlay
  const handleSmartBack = () => {
    console.log('🔙 PlanDetail: handleSmartBack called');
    
    // 🔧 CRITICAL: MyPath uses stateful overlay pattern (line 1980)
    // Must use goBack() to reset navigationState, NOT navigate()
    if (navigationContext) {
      const source = navigationContext.getCurrentSource();
      console.log('🔙 PlanDetail: source =', source);
      
      // If came from another detail page (workout-detail, goal-detail), use navigation history
      if (source === 'workout-detail' || source === 'goal-detail') {
        console.log('🔙 PlanDetail: Going back to detail page');
        navigationContext.goBack();
        return;
      }
      
      console.log('🔙 PlanDetail: Using NavigationContext.goBack() for stateful overlay');
      
      // Dispatch event to MyPath to set viewMode/entityFilter
      if (source === 'today' || source === 'browse-goals' || source === 'browse-plans' || source === 'browse-sessions') {
        console.log('🔙 PlanDetail: Dispatching navigateBackToMyPath event for source:', source);
        window.dispatchEvent(new CustomEvent('navigateBackToMyPath', { 
          detail: { source } 
        }));
      }
      
      // Reset NavigationContext state (closes detail overlay)
      navigationContext.goBack();
      console.log('🔙 PlanDetail: NavigationContext.goBack() called - detail should close');
    } else {
      console.log('🔙 PlanDetail: No NavigationContext - falling back to router.back()');
      // Fallback for route-based navigation
      router.back();
    }
  };
  
  // 🔧 FIX JAN 23: Safe navigation wrapper with fallback
  const safeNavigateToDetail = (type: string, id: string, source: string) => {
    if (navigationContext) {
      navigationContext.navigateToDetail(type as any, id, source);
    } else {
      // Fallback: use react-router navigate
      if (type === 'workout') {
        router.push(`/workout/${id}`);
      } else if (type === 'mobility') {
        router.push(`/mobility/${id}`);
      } else if (type === 'restDay') {
        router.push(`/rest-day/${id}`);
      }
    }
  };
  
  const researchEvent = null; // Set to null since endpoint doesn't exist - hides research section
  
  // 🔄 JAN 21, 2026: Daily routine completion state
  const [dailyRoutineCompletions, setDailyRoutineCompletions] = useState<{
    [mobilityId: string]: {
      monday: boolean;
      tuesday: boolean;
      wednesday: boolean;
      thursday: boolean;
      friday: boolean;
      saturday: boolean;
      sunday: boolean;
    };
  }>({});
  
  const planWithContent = DataService.getPlanWithWorkoutsAndMobility(planId);
  
  // 🎯 JAN 23: Removed userName state - now using profile context directly

  // 🔄 JAN 21, 2026: Fetch daily routine completions on mount and when plan changes
  useEffect(() => {
    const loadDailyCompletions = async () => {
      if (!planWithContent || !planId) return;
      
      const dailyRoutines = planWithContent.mobilityRoutines.filter(
        (m: MobilityRoutine) => m.frequency === 'daily'
      );
      
      if (dailyRoutines.length === 0) return;
      
      console.log(`🔄 PlanDetail: Loading completions for ${dailyRoutines.length} daily routines`);
      
      const completionsMap: typeof dailyRoutineCompletions = {};
      
      for (const routine of dailyRoutines) {
        try {
          const completions = await DataService.getDailyRoutineCompletions(routine.id, planId);
          completionsMap[routine.id] = completions;
          console.log(`✅ Loaded completions for ${routine.title}:`, completions);
        } catch (error) {
          console.error(`❌ Failed to load completions for ${routine.id}:`, error);
          // Use all false as fallback
          completionsMap[routine.id] = {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false
          };
        }
      }
      
      setDailyRoutineCompletions(completionsMap);
    };
    
    loadDailyCompletions();
  }, [planId, planWithContent]);

  if (!planWithContent) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center">
            <button onClick={handleSmartBack} className="mr-3 text-gray-600 hover:text-gray-800 p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Plan Not Found</h1>
          </div>
        </div>
        <div className="p-4">
          <p className="text-gray-600">The requested training plan could not be found.</p>
        </div>
      </div>
    );
  }

  // Get ALL related goals for multi-goal display
  const relatedGoals = planWithContent.goalIds.map(goalId => DataService.getGoalById(goalId)).filter((goal): goal is NonNullable<typeof goal> => goal !== null && goal !== undefined);

  // ✅ Get all sessions for the week (workouts + mobility) sorted by date (reverse chronological)
  const planStart = planWithContent.weekStartDate ? new Date(planWithContent.weekStartDate) : null;
  const planEnd = planWithContent.weekEndDate ? new Date(planWithContent.weekEndDate) : null;
  
  // Normalize dates to day boundaries for accurate comparison
  if (planStart) planStart.setHours(0, 0, 0, 0);
  if (planEnd) planEnd.setHours(23, 59, 59, 999);
  
  // 🆕 PHASE 5: Get rest days for display
  const restDays = planWithContent.restDays || [];
  
  // 🔄 JAN 21, 2026: Separate daily routines from regular sessions
  const dailyRoutines = planWithContent.mobilityRoutines.filter(
    (m: MobilityRoutine) => m.frequency === 'daily'
  );

  // 🔧 MAR 13: Collect rest-day child activities (workouts + mobility with context='rest_day')
  // These will be rendered nested under their parent rest day card instead of as standalone cards
  const restDayChildActivities: { [dateStr: string]: Array<{ type: 'workout' | 'mobility'; item: Workout | MobilityRoutine }> } = {};

  // Collect rest-day child workouts
  planWithContent.workouts.forEach((w: Workout) => {
    if ((w as any).context === 'rest_day' && w.scheduledDate) {
      const dateStr = toLocalDateStr(w.scheduledDate);
      if (!restDayChildActivities[dateStr]) restDayChildActivities[dateStr] = [];
      restDayChildActivities[dateStr].push({ type: 'workout', item: w });
    }
  });

  // Collect rest-day child mobility routines
  planWithContent.mobilityRoutines.forEach((m: MobilityRoutine) => {
    if ((m as any).context === 'rest_day' && m.scheduledDate && m.frequency !== 'daily') {
      const dateStr = toLocalDateStr(m.scheduledDate);
      if (!restDayChildActivities[dateStr]) restDayChildActivities[dateStr] = [];
      restDayChildActivities[dateStr].push({ type: 'mobility', item: m });
    }
  });

  const allSessions = [
    ...planWithContent.workouts
      .filter((w: Workout) => {
        // 🔧 MAR 13: Filter out rest_day child workouts (recovery activities nested under rest day card)
        return (w as any).context !== 'rest_day';
      })
      .map((w: Workout) => ({ ...w, sessionType: 'workout' as const })),
    ...planWithContent.mobilityRoutines
      .filter((m: MobilityRoutine) => {
        // 🔧 JAN 25: Filter out component mobility (warmup/cooldown children)
        // Check BOTH context AND workout_id (either indicates child)
        const isDaily = m.frequency === 'daily';
        const isComponent = m.context === 'component';
        const hasParent = !!(m as any).workout_id;
        // 🔧 MAR 13: Also filter out rest_day child mobility (nested under rest day card)
        const isRestDayChild = (m as any).context === 'rest_day';
        return !isDaily && !isComponent && !hasParent && !isRestDayChild;
      })
      .map((m: MobilityRoutine) => ({ ...m, sessionType: 'mobility' as const })),
    ...restDays.map((r: RestDay) => ({ ...r, sessionType: 'rest' as const })) // 🆕 PHASE 5: Add rest days
  ].filter((session: any) => {
    if (!session.scheduledDate) {
      return true; // Include unscheduled sessions
    }
    // 🔧 M35B FIX: Use toLocalDateStr() to avoid UTC shift from toISOString()
    const sessionDateStr = toLocalDateStr(session.scheduledDate);
    const planStartStr = planStart ? toLocalDateStr(planStart) : null;
    const planEndStr = planEnd ? toLocalDateStr(planEnd) : null;
    
    const isInRange = (!planStartStr || sessionDateStr >= planStartStr) && (!planEndStr || sessionDateStr <= planEndStr);
    
    return isInRange;
  });
  
  // Sort by date (chronological - closest to today first)
  const sortedSessions = allSessions.sort((a: any, b: any) => {
    const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
    const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
    return dateA - dateB; // Chronological order (earliest first)
  });

  // Calculate day dots with workout info (Issue 4d - timezone-aware)
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`; // Local YYYY-MM-DD
  
  // 🔧 JAN 23, 2026: Split sessions into current/future vs past
  // Current/Future: Today's workout + all future workouts (stays at top)
  // Past: Workouts from this week that are now in the past (moves to bottom at midnight)
  const currentAndFutureSessions = sortedSessions.filter((session: any) => {
    if (!session.scheduledDate) return true; // Include unscheduled
    
    let sessionDateStr: string;
    if (session.scheduledDate instanceof Date) {
      const sYear = session.scheduledDate.getFullYear();
      const sMonth = String(session.scheduledDate.getMonth() + 1).padStart(2, '0');
      const sDay = String(session.scheduledDate.getDate()).padStart(2, '0');
      sessionDateStr = `${sYear}-${sMonth}-${sDay}`;
    } else if (typeof session.scheduledDate === 'string') {
      if (session.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        sessionDateStr = session.scheduledDate.substring(0, 10);
      } else {
        const sDate = new Date(session.scheduledDate);
        const sYear = sDate.getFullYear();
        const sMonth = String(sDate.getMonth() + 1).padStart(2, '0');
        const sDay = String(sDate.getDate()).padStart(2, '0');
        sessionDateStr = `${sYear}-${sMonth}-${sDay}`;
      }
    } else {
      return true; // Include if can't determine date
    }
    
    // Include if today or future (>= comparison)
    return sessionDateStr >= todayStr;
  });
  
  const pastSessions = sortedSessions.filter((session: any) => {
    if (!session.scheduledDate) return false; // Unscheduled don't go to past
    
    let sessionDateStr: string;
    if (session.scheduledDate instanceof Date) {
      const sYear = session.scheduledDate.getFullYear();
      const sMonth = String(session.scheduledDate.getMonth() + 1).padStart(2, '0');
      const sDay = String(session.scheduledDate.getDate()).padStart(2, '0');
      sessionDateStr = `${sYear}-${sMonth}-${sDay}`;
    } else if (typeof session.scheduledDate === 'string') {
      if (session.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        sessionDateStr = session.scheduledDate.substring(0, 10);
      } else {
        const sDate = new Date(session.scheduledDate);
        const sYear = sDate.getFullYear();
        const sMonth = String(sDate.getMonth() + 1).padStart(2, '0');
        const sDay = String(sDate.getDate()).padStart(2, '0');
        sessionDateStr = `${sYear}-${sMonth}-${sDay}`;
      }
    } else {
      return false;
    }
    
    // Include if in the past (< comparison)
    return sessionDateStr < todayStr;
  });
  
  // 🔧 FIX JAN 28: Calculate CURRENT CALENDAR WEEK boundaries (Monday-Sunday)
  // Note: today and todayStr already declared above for session splitting (line 274-275)
  const todayDayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
  
  // Calculate Monday of this week
  // If today is Sunday (0), go back 6 days. Otherwise go back (dayOfWeek - 1) days
  const daysToMonday = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - daysToMonday);
  currentWeekStart.setHours(0, 0, 0, 0);
  
  // Calculate Sunday of this week (Monday + 6 days)
  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
  currentWeekEnd.setHours(23, 59, 59, 999);
  
  // Get sessions for each day of the week BY DATE STRING
  // 🔧 FIX FEB 9, 2026: Use date string directly when "YYYY-MM-DD" to avoid UTC shift
  // Problem: new Date("2026-02-10") = UTC midnight → in EST = Feb 9 19:00 → getDate()=9
  // Result: Tuesday workout matched to Monday dot (Monday blue with no workout)
  const getSessionsForDay = (dayDateStr: string) => {
    const matched = allSessions.filter((session: any) => {
      if (!session.scheduledDate) return false;

      let sessionDateStr: string;
      if (typeof session.scheduledDate === 'string' && session.scheduledDate.match(/^\d{4}-\d{2}-\d{2}/)) {
        sessionDateStr = session.scheduledDate.substring(0, 10);
      } else if (session.scheduledDate instanceof Date) {
        const d = session.scheduledDate;
        sessionDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else {
        const sessionDate = new Date(session.scheduledDate);
        sessionDateStr = `${sessionDate.getFullYear()}-${String(sessionDate.getMonth() + 1).padStart(2, '0')}-${String(sessionDate.getDate()).padStart(2, '0')}`;
      }

      return sessionDateStr === dayDateStr;
    });

    return matched;
  };
  
  // Check if all sessions for a day are completed
  const areAllSessionsDone = (sessions: any[]) => {
    if (sessions.length === 0) return false;
    return sessions.every((s: any) => s.status === 'completed');
  };
  
  // Check if any sessions exist for a day
  const hasSessions = (sessions: any[]) => {
    return sessions.length > 0;
  };

  // Calculate categories (same as ThisWeeksPlanCard)
  const cardioCount = sortedSessions.filter((s: any) => s.activityType === 'cardio').length;
  const strengthCount = sortedSessions.filter((s: any) => s.activityType === 'strength').length;
  const hiitCount = sortedSessions.filter((s: any) => s.activityType === 'hiit').length;
  const yogaCount = sortedSessions.filter((s: any) => s.activityType === 'yoga').length;
  const mobilityCount = sortedSessions.filter((s: any) => s.sessionType === 'mobility').length;
  const restCount = 0; // Rest days not included in sessions array

  const categories = [
    { icon: '🏃', label: 'Cardio', count: cardioCount, color: 'text-blue-600' },
    { icon: '🏋️', label: 'Strength', count: strengthCount, color: 'text-purple-600' },
    { icon: '⚡', label: 'HIIT', count: hiitCount, color: 'text-red-600' },
    { icon: '🕉️', label: 'Yoga', count: yogaCount, color: 'text-indigo-600' },
    { icon: '🧘', label: 'Mobility', count: mobilityCount, color: 'text-green-600' },
    { icon: '😴', label: 'Rest', count: restCount, color: 'text-gray-600' }
  ].filter(cat => cat.count > 0);

  const gridClass = categories.length <= 3 
    ? 'flex items-center space-x-4' 
    : 'grid grid-cols-3 gap-2';

  // Get week title and subhead
  const getWeekTitle = () => {
    // 🎯 JAN 23, 2026: Use LLM-generated title if available (personality-aware, motivating)
    // Examples: "Let's get it going!", "Building on that 18K!", "Back into it after being sick"
    if (planWithContent.title && !planWithContent.title.includes('Weekly Plan') && !planWithContent.title.includes('Goal Plan')) {
      return planWithContent.title;
    }
    
    // Fallback to date-based title if no LLM title
    if (!planStart) return planWithContent.title;
    const monthName = planStart.toLocaleDateString('en-US', { month: 'short' });
    const day = planStart.getDate();
    
    // 🔧 JAN 25: Check BOTH camelCase AND snake_case (API inconsistency)
    const userName = (profile as any)?.preferredName || 
                    (profile as any)?.preferred_name || 
                    (profile as any)?.firstName || 
                    (profile as any)?.first_name || 
                    'Your';
    
    // Possessive apostrophe logic:
    // - "Your" → no apostrophe
    // - Names ending in 's' → just apostrophe (Chris' not Chris's)
    // - Other names → apostrophe + s (Paul's)
    let possessive = '';
    if (userName !== 'Your') {
      possessive = userName.endsWith('s') ? "'" : "'s";
    }
    const dayWithSuffix = day === 1 || day === 21 || day === 31 ? `${day}st` 
      : day === 2 || day === 22 ? `${day}nd`
      : day === 3 || day === 23 ? `${day}rd`
      : `${day}th`;
    return `${userName}${possessive} week of ${monthName} ${dayWithSuffix}`;
  };

  // 🔧 FIX JAN 23: Use correct field name - weekOverview (not weekSubhead)
  const weekSubhead = (typeof planWithContent.weekOverview === 'string' ? planWithContent.weekOverview : null) 
    || (typeof planWithContent.description === 'string' ? planWithContent.description : null);

  const handleWorkoutComplete = (workout: Workout) => {
    // Handle workout completion if needed
    console.log('Workout completed:', workout.id);
  };

  // 🔧 MAR 13: Extracted component for rest-day child activities (removes ~70 lines of duplication)
  const RestDayChildActivities: React.FC<{
    childActivities: Array<{ type: 'workout' | 'mobility'; item: Workout | MobilityRoutine }>;
    onNavigate: (type: string, id: string) => void;
  }> = ({ childActivities, onNavigate }) => {
    if (childActivities.length === 0) return null;
    return (
      <div className="ml-4 mt-1 mb-1 border-l-2 border-teal-200 pl-3 space-y-2">
        {childActivities.map((child) => {
          if (child.type === 'workout') {
            const w = child.item as Workout;
            return (
              <div
                key={w.id}
                onClick={() => onNavigate('workout', w.id)}
                className="p-2.5 bg-blue-50 rounded-lg border border-blue-100 cursor-pointer hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">Optional</span>
                      <h4 className="text-sm font-medium text-gray-900 truncate">{w.title}</h4>
                    </div>
                    {w.duration && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {typeof w.duration === 'number' ? `${w.duration} min` : (w.duration || '')}{w.activityType && ` \u2022 ${w.activityType}`}
                      </p>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          } else {
            const m = child.item as MobilityRoutine;
            return (
              <div
                key={m.id}
                onClick={() => onNavigate('mobility', m.id)}
                className="p-2.5 bg-teal-50 rounded-lg border border-teal-100 cursor-pointer hover:bg-teal-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded border border-teal-200">Optional</span>
                      <h4 className="text-sm font-medium text-gray-900 truncate">{m.title}</h4>
                    </div>
                    {m.duration_minutes && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {m.duration_minutes} min
                        {m.exercises && m.exercises.length > 0 && ` \u2022 ${m.exercises.length} exercises`}
                      </p>
                    )}
                  </div>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="clean-chat-container h-screen bg-gray-50 max-w-md mx-auto overflow-y-auto flex flex-col">
      {/* Header Card: Title + Back + Chat + Icons + Dots + Overview */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 flex-shrink-0" style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}>
        {/* Top row: Back + Title + Chat */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
          <button
            onClick={handleSmartBack}
            className="flex items-center text-gray-600 hover:text-gray-900 p-1 -ml-1 flex-shrink-0"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
          
          <h1 className="text-lg font-bold text-gray-900 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
            {getWeekTitle()}
          </h1>
          
          <ContextualChatButton 
            context={{
              type: 'plan',
              id: planWithContent.id,
              title: planWithContent.title,
              description: planWithContent.description
            }}
            text=""
            size="small"
            variant="clean"
            className="px-3 py-1.5 flex-shrink-0"
          />
        </div>

        {/* 🔧 FIX JAN 23, 2026: Day Dots - Consistent with ThisWeeksPlanCard */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex justify-between items-end">
            {Array.from({ length: 7 }, (_, dayIndex) => {
              // dayIndex: 0=Monday, 1=Tuesday, ..., 6=Sunday (currentWeekStart is Monday)
              const dayDate = new Date(currentWeekStart);
              dayDate.setDate(currentWeekStart.getDate() + dayIndex);
              // 🔧 FIX FEB 9, 2026: Use local date string so dots match session dates in all timezones
              // toISOString() is UTC — in Tokyo, Mon 00:00 local = Sun 15:00 UTC → wrong date
              const dayDateStr = `${dayDate.getFullYear()}-${String(dayDate.getMonth() + 1).padStart(2, '0')}-${String(dayDate.getDate()).padStart(2, '0')}`;
              
              const daySessions = getSessionsForDay(dayDateStr);
              const hasWorkouts = hasSessions(daySessions);
              const allDone = areAllSessionsDone(daySessions);
              const isToday = dayDateStr === todayStr;
              const isPast = dayDateStr < todayStr;
              
              // Dot color system (matches native ThisWeeksPlanCard):
              // Dot color system (matches native ThisWeeksPlanCard):
              //   Teal (#42A5A5)         = rest day (always, even if past)
              //   Cedar Prime (#1F6C4F)  = completed past day + white checkmark
              //   Cedar Mist (#A8D1BF)   = missed/incomplete past workout day
              //   Cedar Light (#5FA287)  = today/future with workout
              //   Gray (#E0E0E0)         = no activity scheduled

              // Filter out component mobility FIRST so dot color + icon use the same data
              const filteredSessions = daySessions.filter((s: any) => {
                if (s.sessionType === 'mobility') {
                  if (s.context === 'component' || s.context === 'rest_day' || s.workout_id) return false;
                  const t = (s.title || '').toLowerCase();
                  if (CHILD_MOBILITY_RE.test(t)) return false;
                }
                return true;
              });
              const hasRestDay = filteredSessions.some((s: any) => s.sessionType === 'rest');

              let dotColor = 'bg-[#E0E0E0]'; // Default: no activity
              let showCheckmark = false;

              if (hasRestDay) {
                dotColor = 'bg-[#42A5A5]'; // Teal — rest day (always)
              } else if (isPast && hasWorkouts && allDone) {
                dotColor = 'bg-[#1F6C4F]'; // Cedar Prime — completed
                showCheckmark = true;
              } else if (isPast && hasWorkouts) {
                dotColor = 'bg-[#A8D1BF]'; // Cedar Mist — missed/incomplete
              } else if (hasWorkouts) {
                dotColor = 'bg-[#5FA287]'; // Cedar Light — today/future with workout
              }
              
              // 🎨 FIX JAN 23: Today is 50% larger (consistent with ThisWeeksPlanCard)
              const dotSize = isToday ? 'w-[18px] h-[18px]' : 'w-3 h-3';
              const dayAbbrevs = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
              
              // 🔧 FIX JAN 23: Helper function to get workout icon component (matches MyPath.tsx)
              const getWorkoutIconComponent = (session: any) => {
                // 🔧 JAN 25: Show "REST" badge for rest days (matches ThisWeeksPlanCard)
                if (session.sessionType === 'rest') {
                  return (
                    <span 
                      className="text-[10px] font-semibold px-1 py-0.5 rounded"
                      style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                    >
                      REST
                    </span>
                  );
                }
                
                if (session.sessionType === 'mobility') {
                  const modality = (session.modality || session.type || '').toLowerCase();
                  if (modality === 'yoga' || modality.includes('yoga')) {
                    return <WorkoutIcon type="yoga" size={16} className="flex-shrink-0" />;
                  }
                  if (modality === 'static-stretching' || modality.includes('stretch')) {
                    return <WorkoutIcon type="stretching" size={16} className="flex-shrink-0" />;
                  }
                  return <WorkoutIcon type="stretching" size={16} className="flex-shrink-0" />;
                }
                
                const workoutType = session.type || session.workout_type || '';
                const activityType = session.activityType || '';
                const iconType = getWorkoutIconType(workoutType, activityType);
                
                return (
                  <WorkoutIcon
                    iconType={iconType}
                    title={session.title || session.name || ''}
                    size={16}
                    className="flex-shrink-0"
                  />
                );
              };

              return (
                <div key={dayIndex} className="flex flex-col items-center flex-1">
                  {/* 🎨 Dot with optional checkmark */}
                  <div className="relative mb-1">
                    <div className={`rounded-full transition-all duration-200 ${dotSize} ${dotColor}`} />
                    {showCheckmark && (
                      <svg 
                        className="absolute inset-0 m-auto" 
                        width={isToday ? "12" : "8"} 
                        height={isToday ? "12" : "8"} 
                        viewBox="0 0 12 12" 
                        fill="none"
                      >
                        <path 
                          d="M10 3L4.5 8.5L2 6" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  
                  {/* Primary activity icon below dot — one icon per day (matches native) */}
                  {hasWorkouts && filteredSessions.length > 0 && (
                    <div className="flex items-center gap-px mb-1 justify-center">
                      {(() => {
                        // Rest day takes priority
                        const restSession = filteredSessions.find((s: any) => s.sessionType === 'rest');
                        if (restSession) {
                          return (
                            <span
                              className="text-[10px] font-semibold px-1 py-0.5 rounded"
                              style={{ color: 'var(--cedar-accent-sky)', backgroundColor: 'rgba(66, 165, 165, 0.1)' }}
                            >
                              REST
                            </span>
                          );
                        }
                        // Pick primary: strength > hiit > cardio > mobility
                        // Resolve to canonical icon type FIRST so "full-body-strength" → "weightlifting"
                        const PRIORITY = ['weightlifting', 'bodyweight', 'core', 'crossfit', 'hiit', 'running', 'cycling', 'swimming', 'walking'];
                        const primary = [...filteredSessions]
                          .filter((s: any) => s.sessionType !== 'mobility')
                          .sort((a: any, b: any) => {
                            const aType = getWorkoutIconType(a.type || a.activityType || '', a.title || '');
                            const bType = getWorkoutIconType(b.type || b.activityType || '', b.title || '');
                            const aIdx = PRIORITY.indexOf(aType);
                            const bIdx = PRIORITY.indexOf(bType);
                            return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
                          })[0] || filteredSessions[0];
                        return <div>{getWorkoutIconComponent(primary)}</div>;
                      })()}
                    </div>
                  )}
                  
                  {/* Day Letter - Bold for today */}
                  <span className={`text-xs text-gray-500 ${isToday ? 'font-bold' : 'font-medium'}`}>
                    {dayAbbrevs[dayIndex]}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 🎯 JAN 21, 2026: This Week section - Under dots, normal text (not italic) */}
        {weekSubhead && (
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">This Week</h3>
            <p className="text-sm text-gray-700 leading-relaxed">{weekSubhead}</p>
          </div>
        )}
      </div>

      {/* Content - Natural scroll under fixed header */}
      <div className="flex-1 overflow-y-auto p-4 pb-32">
        {/* 📊 METRIC PROGRESS (if available) */}
        {planWithContent?.metricAssessment && planWithContent.metricAssessment.status !== 'no_data' && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Progress This Week</h2>
            
            <div className={`inline-block px-3 py-1 rounded-full text-sm mb-3 ${
              planWithContent.metricAssessment.status === 'on_track' ? 'bg-green-100 text-green-800' :
              planWithContent.metricAssessment.status === 'ahead' ? 'bg-blue-100 text-blue-800' :
              'bg-yellow-100 text-yellow-800'
            }`}>
              {planWithContent.metricAssessment.status === 'on_track' ? '✓ On Track' :
               planWithContent.metricAssessment.status === 'ahead' ? '↗ Ahead' :
               '⚠ Behind'}
            </div>
            
            <p className="text-gray-700 mb-3">
              {planWithContent.metricAssessment.narrative}
            </p>
            
            {planWithContent.metricAssessment.comparison && planWithContent.metricAssessment.comparison.length > 0 && (
              <div className="space-y-2">
                {planWithContent.metricAssessment.comparison.map((comp, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-gray-600 capitalize">{comp.metric.replace('_', ' ')}</span>
                    <span className="font-medium">
                      {comp.actual} 
                      <span className="text-gray-400 mx-2">vs</span>
                      {comp.target}
                    </span>
                  </div>
                ))}
              </div>
            )}
            
            {planWithContent.metricAssessment.adjustments && (
              <div className="mt-3 p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Adjustments: </span>
                  {planWithContent.metricAssessment.adjustments}
                </p>
              </div>
            )}
          </div>
        )}

        {/* 🔄 JAN 21, 2026: DAILY ROUTINES SECTION (Persistent at Top) */}
        {dailyRoutines.length > 0 && (
          <div style={{ 
            marginBottom: 'var(--spacing-2xl)',
            paddingBottom: 'var(--spacing-lg)',
            borderBottom: '2px solid var(--cedar-limestone)'
          }}>
            <h3 
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--coloron-surface900)',
                marginBottom: 'var(--spacing-md)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-sm)'
              }}
            >
              <span>🔄</span> Daily Routines
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
              {dailyRoutines.map((routine: MobilityRoutine) => {
                // ✅ Use real completion status from state (loaded via useEffect)
                const completionStatus = dailyRoutineCompletions[routine.id] || {
                  monday: false,
                  tuesday: false,
                  wednesday: false,
                  thursday: false,
                  friday: false,
                  saturday: false,
                  sunday: false
                };
                
                return (
                  <DailyRoutineCard
                    key={routine.id}
                    routine={routine}
                    isOptional={routine.metadata?.is_optional || false}
                    completionStatus={completionStatus}
                    onTap={() => safeNavigateToDetail('mobility', routine.id, 'plan-detail')}
                    onToggleCompletion={async (date) => {
                      // ✅ Real API call to toggle completion
                      try {
                        console.log(`🔄 Toggling completion for ${routine.id} on ${date}`);
                        
                        const result = await DataService.toggleDailyRoutineCompletion(routine.id, date);
                        
                        if (result.success) {
                          // Update local state optimistically
                          setDailyRoutineCompletions(prev => {
                            const dayName = new Date(date).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as keyof typeof completionStatus;
                            
                            return {
                              ...prev,
                              [routine.id]: {
                                ...prev[routine.id],
                                [dayName]: result.completed
                              }
                            };
                          });
                          
                          console.log(`✅ Toggled ${date} to ${result.completed}`);
                        }
                      } catch (error) {
                        console.error(`❌ Failed to toggle completion:`, error);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* 🎯 ALL SESSIONS LIST - Split into Current/Future and Past */}
        <div className="space-y-3">
          {/* Current and Future Sessions */}
          {currentAndFutureSessions.length > 0 ? (
            currentAndFutureSessions.map((session: any) => {
              if (session.sessionType === 'workout') {
                return (
                  <SimpleWorkoutCard
                    key={session.id}
                    workout={session as Workout}
                    isArchived={false}
                    onTap={() => safeNavigateToDetail('workout', session.id, 'plan-detail')}
                    onLongPress={() => {}}
                    onComplete={handleWorkoutComplete}
                    showTodayBadge={false}
                    showDateBadge={true}
                  />
                );
              } else if (session.sessionType === 'mobility') {
                return (
                  <SimpleMobilityCard
                    key={session.id}
                    routine={session as MobilityRoutine}
                    isArchived={false}
                    onTap={() => safeNavigateToDetail('mobility', session.id, 'plan-detail')}
                    onLongPress={() => {}}
                    onComplete={(routine) => {
                      console.log('Mobility routine completed:', routine.id);
                    }}
                    showTodayBadge={false}
                    showDateBadge={true}
                  />
                );
              } else if (session.sessionType === 'rest') {
                // 🆕 JAN 23, 2026: Rest day (teal background applied in card itself)
                // 🔧 MAR 13: Get rest day date for child activity lookup
                const restDateStr = session.scheduledDate ? toLocalDateStr(session.scheduledDate) : '';
                const childActivities = restDayChildActivities[restDateStr] || [];
                return (
                  <div key={session.id}>
                    <RestDayCardWrapper
                      restDay={session as RestDay}
                      onTap={() => {
                        safeNavigateToDetail('restDay', session.id, 'plan-detail');
                      }}
                      showDateBadge={true}
                    />
                    <RestDayChildActivities
                      childActivities={childActivities}
                      onNavigate={(type, id) => safeNavigateToDetail(type, id, 'plan-detail')}
                    />
                  </div>
                );
              }
              return null;
            })
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
              <p className="text-gray-600">No sessions scheduled for this week.</p>
            </div>
          )}
          
          {/* 🆕 JAN 23, 2026: Divider + Past Sessions from This Week */}
          {pastSessions.length > 0 && (
            <>
              {/* Divider with "Past sessions" label and "Browse All" button */}
              <div className="flex items-center justify-between pt-4 pb-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Past sessions
                </span>
                <button
                  onClick={() => {
                    // Navigate to browse sessions page
                    window.dispatchEvent(new CustomEvent('navigateBackToMyPath', { 
                      detail: { 
                        source: 'plan-detail',
                        targetView: 'browse',
                        targetEntity: 'sessions'
                      } 
                    }));
                    router.push('/mypath');
                  }}
                  className="text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
                  style={{
                    color: 'var(--cedar-primary)',
                    backgroundColor: 'rgba(46, 94, 52, 0.08)',
                  }}
                >
                  Browse All
                </button>
              </div>
              
              {/* Past Sessions List */}
              {pastSessions.map((session: any) => {
                if (session.sessionType === 'workout') {
                  return (
                    <SimpleWorkoutCard
                      key={session.id}
                      workout={session as Workout}
                      isArchived={false}
                      onTap={() => safeNavigateToDetail('workout', session.id, 'plan-detail')}
                      onLongPress={() => {}}
                      onComplete={handleWorkoutComplete}
                      showTodayBadge={false}
                      showDateBadge={true}
                    />
                  );
                } else if (session.sessionType === 'mobility') {
                  return (
                    <SimpleMobilityCard
                      key={session.id}
                      routine={session as MobilityRoutine}
                      isArchived={false}
                      onTap={() => safeNavigateToDetail('mobility', session.id, 'plan-detail')}
                      onLongPress={() => {}}
                      onComplete={(routine) => {
                        console.log('Mobility routine completed:', routine.id);
                      }}
                      showTodayBadge={false}
                      showDateBadge={true}
                    />
                  );
                } else if (session.sessionType === 'rest') {
                  // 🆕 JAN 23, 2026: Rest day (teal background applied in card itself)
                  // 🔧 MAR 13: Get rest day date for child activity lookup (past sessions)
                  const restDateStr = session.scheduledDate ? toLocalDateStr(session.scheduledDate) : '';
                  const childActivities = restDayChildActivities[restDateStr] || [];
                  return (
                    <div key={session.id}>
                      <RestDayCardWrapper
                        restDay={session as RestDay}
                        onTap={() => {
                          safeNavigateToDetail('restDay', session.id, 'plan-detail');
                        }}
                        showDateBadge={true}
                      />
                      <RestDayChildActivities
                        childActivities={childActivities}
                        onNavigate={(type, id) => safeNavigateToDetail(type, id, 'plan-detail')}
                      />
                    </div>
                  );
                }
                return null;
              })}
            </>
          )}
        </div>

        {/* 🔬 RESEARCH TRANSPARENCY: Research section (always visible at bottom) */}
        {researchEvent && (
          <div className="space-y-4 mt-6">
            <div className="bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-gray-900">Research-Backed Planning</h2>
                <span className="text-xs text-gray-500">{researchEvent.source_count || 0} sources</span>
              </div>
              
              <p className="text-gray-600 text-sm mb-3">{researchEvent.event_description}</p>

              {researchEvent.analysis_summary && researchEvent.analysis_summary.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 mb-2 text-sm">How Research Informed This Week:</h3>
                  <ul className="space-y-1.5">
                    {researchEvent.analysis_summary.map((bullet: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-green-600 font-bold">•</span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="mt-4 pt-3 border-t border-gray-200">
                <ContextualChatButton
                  context={{
                    type: 'plan',
                    id: planId,
                    title: planWithContent?.title || 'Weekly Plan',
                    description: 'Research sources for this plan',
                    action: 'question'
                  }}
                  text=""
                  size="small"
                  variant="secondary"
                  customMessage={`Tell me more about the research behind this week's plan. What sources did you use and how did they inform the training distribution?`}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 🔧 FIX JAN 21: Add default export for App.tsx routing
export default PlanDetail;
